import { getFormProps, getTextareaProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useFetcher } from 'react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button } from '../button/button'
import { StatusButton } from '../button/status-button'
import { AuthContainerHeader } from '../container/auth-container'
import { TextareaField } from '../forms/forms'
import LoginModal from '../login-modal/login-modal'
import Modal from '../modal/modal'
import { Spacer } from '../spacer/spacer'
import Text from '~/components/text/text'
import { useIsFetching } from '~/utils/misc'
import { useOptionalUser } from '~/utils/user'
import type { EventBase } from '~/types/event'
export const changeEventOwnershipFormSchema = z.object({
	eventId: z.string(),
	motivation: z.string({
		required_error: 'form_error_motivation_required',
	}),
})

export default function EventOwnershipRequestBlock({
	event,
}: {
	event: EventBase
}) {
	const fetcher = useFetcher()
	const isPending = useIsFetching(fetcher)
	const [openModal, setOpenModal] = useState(false)
	const [loginModalOpen, setLoginModalOpen] = useState(false)
	const { t } = useTranslation()
	const [form, fields] = useForm({
		id: 'request-ownership',
		constraint: getZodConstraint(changeEventOwnershipFormSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: changeEventOwnershipFormSchema })
		},
		shouldRevalidate: 'onInput',
	})

	const user = useOptionalUser()
	if (user && user.id === event.owner.id) {
		return undefined
	}

	return (
		<div className="text-center">
			{openModal && (
				<Modal
					onClose={() => setOpenModal(false)}
					title={
						fetcher?.data?.result.status !== 'success'
							? t('owner_change_request')
							: undefined
					}
				>
					{fetcher?.data?.result.status !== 'success' ? (
						<fetcher.Form method="POST" {...getFormProps(form)}>
							<input name="formType" value="change" hidden readOnly />
							<input hidden readOnly name="eventId" value={event.id} />
							<Text className="mb-4">{t('owner_change_admin_is_owner')}</Text>
							<TextareaField
								className="col-span-12"
								labelProps={{
									htmlFor: fields.motivation.id,
									children: t('form_label_motivation'),
								}}
								textareaProps={{
									...getTextareaProps(fields.motivation),
								}}
								errors={fields.motivation.errors}
							/>
							<StatusButton
								type="submit"
								variant="secondary"
								status={isPending ? 'pending' : 'idle'}
								disabled={isPending}
							>
								{t('owner_change_request_change')}
							</StatusButton>
						</fetcher.Form>
					) : (
						<>
							<AuthContainerHeader
								title={t('owner_change_thanks_for_request')}
								text={t('owner_change_request_sent')}
								icon="check-badge"
							/>
						</>
					)}
				</Modal>
			)}

			{event.ownerChangeRequested ? (
				<>
					<Text as="sm" className="font-semibold text-success">
						{t('owner_change_already_requested')}
					</Text>
					<Spacer size="4xs" />
				</>
			) : (
				<>
					{event.owner.isAdmin && (
						<div>
							{user ? (
								<>
									<Button
										type="button"
										variant="link"
										onClick={() => setOpenModal(true)}
									>
										{t('owner_change_is_event_yours')}
									</Button>
								</>
							) : (
								<div className="flex justify-center">
									<Button
										onClick={() => setLoginModalOpen(!loginModalOpen)}
										size="md"
										variant="link"
									>
										{t('owner_change_is_event_yours')}
									</Button>
									{loginModalOpen && (
										<LoginModal
											initialPage={0}
											toggleLoginModal={() =>
												setLoginModalOpen(!loginModalOpen)
											}
											text={t('owner_change_login')}
										/>
									)}
								</div>
							)}
						</div>
					)}
				</>
			)}
		</div>
	)
}
